/**
 * How the app's worker and its pages tell each other which build they run
 * (issue #66).
 *
 * A new build's worker takes over only when every open page answers with its
 * build. A page of an older build answers with that build, and a page of a
 * build from before #66 cannot answer at all. Either one keeps the worker
 * waiting: taking over would reload that page, since its update code reloads
 * when the worker in charge changes, and would remove from the cache the files
 * it still loads.
 */

/** Asks a page or a worker which build it runs. Carries a port for the answer. */
export const KUDU_BUILD_QUERY = 'KUDU_BUILD_QUERY';
/** The answer: `{ build, type }`. */
export const KUDU_BUILD_ANSWER = 'KUDU_BUILD_ANSWER';
/** A page of the waiting worker's build asks it to take over if every open page runs that build. */
export const KUDU_TAKE_OVER_IF_CURRENT = 'KUDU_TAKE_OVER_IF_CURRENT';
/** The worker's answer, when the request carried a port: `{ takingOver, type }`. */
export const KUDU_TAKE_OVER_ANSWER = 'KUDU_TAKE_OVER_ANSWER';

/** A window or a worker: whatever a build query can be posted to. */
export interface BuildQueryTarget {
  postMessage(message: unknown, transfer: Transferable[]): void;
}

/**
 * Asks a window or a worker which build it runs. Resolves with its answer, or
 * with null when it gives none within `timeoutMs` or is gone.
 */
export function askBuild(target: BuildQueryTarget, timeoutMs: number): Promise<string | null> {
  return new Promise((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => finish(null), timeoutMs);

    function finish(build: string | null) {
      clearTimeout(timer);
      channel.port1.close();
      resolve(build);
    }

    channel.port1.onmessage = (event: MessageEvent<{ build?: unknown; type?: unknown } | null>) => {
      if (event.data?.type === KUDU_BUILD_ANSWER && typeof event.data.build === 'string') {
        finish(event.data.build);
      }
    };

    try {
      target.postMessage({ type: KUDU_BUILD_QUERY }, [channel.port2]);
    } catch {
      finish(null);
    }
  });
}

/**
 * The windows a worker of this scope can be in charge of. The others (sign-in,
 * marketing and help pages) are in no worker's charge: a take-over neither
 * reloads them nor removes the files they load, so they need not be asked.
 */
export function windowsInScope<Target extends { url: string }>(
  windows: readonly Target[],
  scope: string,
): Target[] {
  return windows.filter((target) => target.url.startsWith(scope));
}

/** Whether every window answers with this build. True when no window is open. */
export async function everyWindowRuns<Target>(
  build: string,
  windows: readonly Target[],
  ask: (target: Target) => Promise<string | null>,
): Promise<boolean> {
  const answers = await Promise.all(windows.map(ask));
  return answers.every((answer) => answer === build);
}
